import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useRealtimeEvent } from "../contexts/RealtimeContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { CreateEntryDialog } from "../components/CreateEntryDialog";
import { MemoryCardDialog } from "../components/MemoryCardDialog";
import { StatusBadge, STATUS_OPTIONS } from "../components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AuthImage } from "../components/AuthImage";
import { toast } from "sonner";
import { Heart, ThumbsUp, Sparkles, Flame, Smile, Repeat, Lock, MapPin, Hotel, ShoppingCart, Link2, Pencil, Trash2, MessageCircle, Send } from "lucide-react";

const REACTIONS = [
  { emoji: "heart", Icon: Heart },
  { emoji: "fire", Icon: Flame },
  { emoji: "sparkle", Icon: Sparkles },
  { emoji: "wow", Icon: Smile },
];

export default function EntryDetail({ entryId, onClose, onChanged }) {
  const { user } = useAuth();
  const [entry, setEntry] = useState(null);
  const [edit, setEdit] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [comment, setComment] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/entries/${entryId}`);
      setEntry(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, [entryId]);

  // Re-fetch entry detail when partner mutates this specific entry
  useRealtimeEvent(
    ["entry:updated", "entry:vote", "entry:reaction", "entry:comment"],
    (data, actorId) => {
      if (actorId === user?.id) return;
      if (data?.id === entryId) load();
    }
  );

  const updateStatus = async (status) => {
    try {
      const previousStatus = entry?.status;
      const { data } = await api.patch(`/entries/${entryId}`, { status });
      setEntry(data);
      onChanged && onChanged();
      toast.success("Status aktualisiert");

      // Memory Card: when transitioning TO experienced, ask for hero photo
      if (status === "experienced" && previousStatus !== "experienced") {
        const imgs = data.images || [];
        if (imgs.length === 1 && !data.hero_image) {
          // auto-set the only available image
          try {
            const { data: d2 } = await api.patch(`/entries/${entryId}`, { hero_image: imgs[0] });
            setEntry(d2);
            onChanged && onChanged();
          } catch (e) { /* noop */ }
        } else if (imgs.length >= 2 && !data.hero_image) {
          setMemoryOpen(true);
        }
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleVote = async () => {
    if (!entry) return;
    const hasVoted = (entry.votes || []).some((v) => v.user_id === user?.id);
    try {
      const { data } = await api.post(`/entries/${entryId}/vote`, { vote: !hasVoted });
      setEntry({ ...entry, votes: data.votes });
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleReaction = async (emoji) => {
    try {
      const { data } = await api.post(`/entries/${entryId}/reaction`, { emoji });
      setEntry({ ...entry, reactions: data.reactions });
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      const { data } = await api.post(`/entries/${entryId}/comments`, { text: comment });
      setEntry({ ...entry, comments: [...(entry.comments || []), data] });
      setComment("");
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    try {
      await api.delete(`/entries/${entryId}`);
      toast.success("Eintrag gelöscht");
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const isOwner = entry && entry.created_by === user?.id;
  const isLocked = entry && entry.is_locked;
  const hasVoted = entry && (entry.votes || []).some((v) => v.user_id === user?.id);
  const myReactions = entry ? (entry.reactions || []).filter((r) => r.user_id === user?.id).map((r) => r.emoji) : [];

  return (
    <Sheet open={!!entryId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-alabaster overflow-y-auto" data-testid="entry-detail-sheet">
        <SheetHeader>
          <SheetTitle className="sr-only">{entry?.title || "Eintrag"}</SheetTitle>
        </SheetHeader>
        {!entry ? (
          <div className="py-12 text-center text-text_secondary">Lade …</div>
        ) : isLocked ? (
          <div className="py-24 text-center px-6">
            <Lock size={28} className="mx-auto mb-4 text-text_secondary" />
            <h2 className="font-serif text-3xl text-ink">Eine Überraschung wartet auf dich</h2>
            <p className="text-text_secondary mt-3">Dein:e Partner:in plant etwas Besonderes für euch beide.</p>
          </div>
        ) : (
          <>
            <div className="space-y-6 mt-2">
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={entry.status} />
                {entry.type === "recurring" && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-text_secondary border border-border rounded-full px-2 py-0.5">
                    <Repeat size={10} /> {entry.rhythm} {entry.interval > 1 ? `· alle ${entry.interval}` : ""}
                  </span>
                )}
                {entry.is_secret && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-terracotta border border-[#E0B4A1] rounded-full px-2 py-0.5">
                    <Lock size={10} /> Geheim
                  </span>
                )}
                {entry.priority === "high" && (
                  <span className="text-[10px] uppercase tracking-[0.2em] text-terracotta">Hohe Priorität</span>
                )}
              </div>

              <h1 className="font-serif text-3xl sm:text-4xl text-ink leading-tight tracking-tight">{entry.title}</h1>
              {entry.description && <p className="text-text_secondary leading-relaxed whitespace-pre-line">{entry.description}</p>}

              {(entry.images || []).length > 0 && (
                <div className="grid grid-cols-2 gap-2" data-testid="entry-detail-gallery">
                  {(entry.images || []).map((fid, idx) => {
                    const isHero = entry.hero_image === fid;
                    return (
                      <div key={fid} className={`relative aspect-square rounded-xl overflow-hidden bg-bone ${isHero ? "ring-2 ring-offset-2 ring-terracotta" : ""}`}>
                        <AuthImage fileId={fid} size="medium" className="w-full h-full object-cover" alt={`image-${idx}`} />
                        {isHero && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-terracotta text-white text-[10px] uppercase tracking-[0.2em] flex items-center gap-1" data-testid={`hero-badge-${fid}`}>
                            <Heart size={10} /> Erinnerung
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {entry.status === "experienced" && (entry.images || []).length >= 2 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full self-start"
                  onClick={() => setMemoryOpen(true)}
                  data-testid="open-memory-picker-button"
                >
                  <Heart size={13} className="mr-1.5 text-terracotta" />
                  {entry.hero_image ? "Erinnerungsfoto ändern" : "Erinnerungsfoto wählen"}
                </Button>
              )}

              {(entry.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {entry.tags.map((t) => (
                    <span key={t} className="text-[11px] uppercase tracking-wider text-text_secondary bg-bone rounded-full px-2.5 py-1">{t}</span>
                  ))}
                </div>
              )}

              {/* Status Selector */}
              <div className="bg-white border border-border rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary mb-2">Status ändern</div>
                <Select value={entry.status} onValueChange={updateStatus}>
                  <SelectTrigger className="rounded-full bg-transparent border-border" data-testid="entry-detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Wish & Vote */}
              <div className="bg-white border border-border rounded-2xl p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <Heart size={16} className="text-terracotta" />
                    <div>
                      <div className="text-sm text-ink">
                        {entry.wish_owner === "both" ? "Beider Wunsch" : entry.wish_owner === "a" ? `${entry.created_by === user?.id ? "Mein" : "Sein/ihr"} Wunsch` : `${entry.created_by === user?.id ? "Wunsch der Partner:in" : "Mein Wunsch"}`}
                      </div>
                      <div className="text-xs text-text_secondary">{(entry.votes || []).length} Stimme{(entry.votes || []).length === 1 ? "" : "n"}</div>
                    </div>
                  </div>
                  <Button onClick={toggleVote} variant={hasVoted ? "default" : "outline"} size="sm" className={`rounded-full ${hasVoted ? "bg-ink text-alabaster" : ""}`} data-testid="entry-vote-button">
                    <ThumbsUp size={13} className="mr-1.5" /> {hasVoted ? "Will ich auch" : "Will ich auch"}
                  </Button>
                </div>
              </div>

              {/* Links */}
              {(entry.map_link || entry.hotel_link || entry.amazon_link || entry.external_link) && (
                <div className="bg-white border border-border rounded-2xl p-5 space-y-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary">Links</div>
                  {entry.map_link && <LinkRow Icon={MapPin} href={entry.map_link} label="Auf Maps öffnen" />}
                  {entry.hotel_link && <LinkRow Icon={Hotel} href={entry.hotel_link} label="Hotel ansehen" />}
                  {entry.amazon_link && <LinkRow Icon={ShoppingCart} href={entry.amazon_link} label="Shop besuchen" />}
                  {entry.external_link && <LinkRow Icon={Link2} href={entry.external_link} label="Link öffnen" />}
                </div>
              )}

              {entry.notes && (
                <div className="bg-white border border-border rounded-2xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary mb-2">Notizen</div>
                  <p className="text-ink whitespace-pre-line">{entry.notes}</p>
                </div>
              )}

              {/* Reactions */}
              <div className="bg-white border border-border rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary mb-3">Reaktionen</div>
                <div className="flex gap-2 flex-wrap">
                  {REACTIONS.map(({ emoji, Icon }) => {
                    const count = (entry.reactions || []).filter((r) => r.emoji === emoji).length;
                    const mine = myReactions.includes(emoji);
                    return (
                      <button
                        key={emoji}
                        onClick={() => toggleReaction(emoji)}
                        data-testid={`reaction-${emoji}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border transition ${mine ? "bg-ink text-alabaster border-ink" : "bg-transparent border-border hover:border-ink"}`}
                      >
                        <Icon size={14} /> {count > 0 && <span className="text-xs">{count}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Comments */}
              <div className="bg-white border border-border rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-text_secondary mb-3 flex items-center gap-1.5">
                  <MessageCircle size={12} /> Kommentare ({(entry.comments || []).length})
                </div>
                <div className="space-y-3 mb-3 max-h-48 overflow-y-auto">
                  {(entry.comments || []).map((c) => (
                    <div key={c.id} className="border-l-2 border-bone pl-3" data-testid={`comment-${c.id}`}>
                      <div className="text-xs text-text_secondary mb-0.5">{c.user_name}</div>
                      <div className="text-sm text-ink">{c.text}</div>
                    </div>
                  ))}
                  {(entry.comments || []).length === 0 && <div className="text-sm text-text_secondary">Noch keine Kommentare.</div>}
                </div>
                <form onSubmit={submitComment} className="flex gap-2">
                  <input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Schreib etwas …"
                    className="flex-1 bg-bone rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ink"
                    data-testid="comment-input"
                  />
                  <Button type="submit" disabled={!comment.trim()} size="icon" className="rounded-full bg-ink text-alabaster" data-testid="comment-submit">
                    <Send size={14} />
                  </Button>
                </form>
              </div>

              {/* Actions */}
              {isOwner && (
                <div className="flex gap-2 pt-2">
                  <Button onClick={() => setEdit(true)} variant="outline" className="rounded-full flex-1" data-testid="entry-edit-button">
                    <Pencil size={13} className="mr-2" /> Bearbeiten
                  </Button>
                  <Button onClick={handleDelete} variant="ghost" className="rounded-full text-terracotta hover:text-terracotta hover:bg-[#F5E2DA]" data-testid="entry-delete-button">
                    <Trash2 size={13} className="mr-2" /> Löschen
                  </Button>
                </div>
              )}
            </div>
            {edit && <CreateEntryDialog open={edit} onOpenChange={setEdit} listId={entry.list_id} initial={entry} onCreated={(updated) => { setEntry(updated); onChanged && onChanged(); }} />}
            <MemoryCardDialog
              open={memoryOpen}
              onOpenChange={setMemoryOpen}
              entry={entry}
              onSelected={(updated) => { setEntry(updated); onChanged && onChanged(); }}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const LinkRow = ({ Icon, href, label }) => (
  <a href={href} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm text-ink hover:text-terracotta transition-colors py-1">
    <Icon size={14} className="text-text_secondary" /> {label}
  </a>
);
